import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Loader2, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KEY_COLORS = {
  'C': '#ff6b6b', 'C#': '#ff8e53', 'D': '#feca57', 'Eb': '#48dbfb',
  'E': '#0abde3', 'F': '#10ac84', 'F#': '#1dd1a1', 'G': '#54a0ff',
  'Ab': '#5f27cd', 'A': '#c44dff', 'Bb': '#ff6348', 'B': '#ee5a24'
};
const CHROMA = ['C','C#','D','Eb','E','F','F#','G','Ab','A','Bb','B'];

const BeatAnalyzer = ({ toolContext, onAnalysisComplete }) => {
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [cachedFilename, setCachedFilename] = useState(null);
  const [showCachedBanner, setShowCachedBanner] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const checkCache = async () => {
      try {
        const token = localStorage.getItem('beatseek_token');
        const res = await fetch(`${API}/tools/beat-analysis-cache`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success && data.analysis) {
          const created = new Date(data.created_at);
          if (created.getTime() > Date.now() - 3600000) {
            setAnalysis(data.analysis);
            setCachedFilename(data.filename);
            setShowCachedBanner(true);
            if (onAnalysisComplete) onAnalysisComplete(data.analysis);
          }
        }
      } catch (e) { /* no cache */ }
    };
    checkCache();
  }, []);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > 10 * 1024 * 1024) { alert('File too large. Maximum 10MB.'); return; }
    setFile(selected);
    setAnalysis(null);
    setAiSuggestions(null);
    setShowCachedBanner(false);
    setExpanded(true);
    handleUpload(selected);
  };

  const handleUpload = async (audioFile) => {
    setAnalyzing(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const formData = new FormData();
      formData.append('file', audioFile);
      const res = await fetch(`${API}/tools/analyze-beat`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Analysis failed'); }
      const data = await res.json();
      setAnalysis(data.analysis);
      setCachedFilename(data.filename);
      if (onAnalysisComplete) onAnalysisComplete(data.analysis);
    } catch (err) { alert(`Analysis error: ${err.message}`); }
    finally { setAnalyzing(false); }
  };

  const getAISuggestions = async () => {
    if (!analysis) return;
    setLoadingAI(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const res = await fetch(`${API}/tools/beat-ai-suggest`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysis, tool_context: toolContext })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'AI suggestions failed'); }
      const data = await res.json();
      setAiSuggestions(data.data);
    } catch (err) { alert(`AI error: ${err.message}`); }
    finally { setLoadingAI(false); }
  };

  const clearAnalysis = () => {
    setFile(null); setAnalysis(null); setAiSuggestions(null);
    setShowCachedBanner(false); setCachedFilename(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div
      className="mb-3 beat-analyzer-border"
      data-testid="beat-analyzer"
    >
      <div className="beat-analyzer-inner">
      {/* Header row with upload button */}
      <div className="flex items-center justify-between px-3 py-2">
        <div
          className="flex items-center gap-1.5 cursor-pointer select-none flex-1"
          onClick={() => setExpanded(!expanded)}
        >
          <Sparkles size={12} className="text-cyan-400" style={{ filter: 'drop-shadow(0 0 4px rgba(6,182,212,0.5))' }} />
          <span className="text-xs font-semibold text-cyan-400">Analyze My Beat</span>
          <span className="text-[8px] px-1 py-0.5 rounded" style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.15), rgba(236,72,153,0.15))', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.2)' }}>AI</span>
          {analysis && !expanded && (
            <span className="text-[9px] bg-cyan-500/10 text-cyan-400/80 px-1.5 py-0.5 rounded ml-1">
              {analysis.key_full} | {analysis.bpm} BPM
            </span>
          )}
          {expanded ? <ChevronUp size={12} className="text-gray-600 ml-1" /> : <ChevronDown size={12} className="text-gray-600 ml-1" />}
        </div>

        {/* Always-visible upload button - magenta theme */}
        <button
          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
          className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-semibold transition-all hover:brightness-110"
          style={{
            background: 'linear-gradient(135deg, rgba(234,179,8,0.2), rgba(245,158,11,0.2))',
            border: '1px solid rgba(234,179,8,0.45)',
            color: '#fbbf24',
            boxShadow: '0 0 8px rgba(234,179,8,0.15)'
          }}
          data-testid="beat-upload-btn"
        >
          <Upload size={11} />
          {analyzing ? 'Analyzing...' : 'Upload Beat'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={handleFileSelect}
          data-testid="beat-file-input"
        />
      </div>

      {/* Expandable content */}
      {expanded && (
        <div className="px-3 pb-3 space-y-2" style={{ borderTop: '1px solid rgba(6,182,212,0.08)' }}>

          {/* Cached banner */}
          {showCachedBanner && !file && (
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded text-[10px] mt-2"
              style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}
              data-testid="cached-analysis-banner"
            >
              <Sparkles size={10} className="text-purple-400 shrink-0" />
              <span className="text-purple-300">
                Using <strong>{cachedFilename || 'recent upload'}</strong>
              </span>
              <button onClick={clearAnalysis} className="ml-auto text-purple-400 hover:text-white"><X size={10} /></button>
            </div>
          )}

          {/* Analyzing spinner */}
          {analyzing && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 size={16} className="text-cyan-400 animate-spin" />
              <span className="text-xs text-cyan-300">Analyzing...</span>
            </div>
          )}

          {/* No analysis yet prompt */}
          {!analysis && !analyzing && (
            <div className="text-center py-3">
              <p className="text-[11px] text-gray-500">Upload a short beat clip (30s max) to detect key, BPM & get AI suggestions</p>
            </div>
          )}

          {/* Analysis results - compact */}
          {analysis && (
            <div className="space-y-2 mt-1">
              {/* Compact metrics row */}
              <div className="flex gap-1.5">
                <div className="flex-1 rounded px-2 py-1.5 text-center" style={{ background: 'rgba(6,182,212,0.06)', border: '1px solid rgba(6,182,212,0.12)' }}>
                  <div className="text-sm font-bold" style={{ color: KEY_COLORS[analysis.key] || '#00d4ff' }}>{analysis.key_full}</div>
                  <div className="text-[8px] text-gray-600">{Math.round(analysis.confidence * 100)}% conf</div>
                </div>
                <div className="flex-1 rounded px-2 py-1.5 text-center" style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.12)' }}>
                  <div className="text-sm font-bold text-purple-400">{analysis.bpm}</div>
                  <div className="text-[8px] text-gray-600">BPM</div>
                </div>
                <div className="flex-1 rounded px-2 py-1.5 text-center" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.12)' }}>
                  <div className="text-sm font-bold text-emerald-400">
                    {analysis.energy > 0.08 ? 'High' : analysis.energy > 0.04 ? 'Med' : 'Low'}
                  </div>
                  <div className="text-[8px] text-gray-600">Energy</div>
                </div>
              </div>

              {/* Compact chroma bars */}
              {analysis.chroma_profile && (
                <div className="flex items-end gap-px h-8">
                  {analysis.chroma_profile.map((val, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full rounded-sm"
                        style={{
                          height: `${Math.max(val * 32, 1)}px`,
                          background: analysis.dominant_notes?.includes(CHROMA[i])
                            ? (KEY_COLORS[CHROMA[i]] || '#00d4ff')
                            : 'rgba(148,163,184,0.15)',
                          opacity: val > 0.3 ? 1 : 0.4
                        }}
                      />
                      <span className="text-[6px] text-gray-700 mt-px leading-none">{CHROMA[i]}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* AI button */}
              <button
                onClick={getAISuggestions}
                disabled={loadingAI}
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-semibold transition-all hover:brightness-125"
                style={{
                  background: 'linear-gradient(135deg, #7c3aed, #9333ea)',
                  border: '1px solid rgba(167,139,250,0.5)',
                  color: '#ffffff',
                  boxShadow: '0 0 12px rgba(139,92,246,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                }}
                data-testid="get-ai-suggestions-btn"
              >
                {loadingAI ? (
                  <><Loader2 size={12} className="animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles size={12} /> {toolContext === 'chord_progressions' ? 'Get Chord Suggestions' : 'Get Scale Suggestions'}</>
                )}
              </button>

              {/* AI results - compact cards */}
              {aiSuggestions && (
                <div className="space-y-1.5" data-testid="ai-suggestions-results">
                  {aiSuggestions.detected_summary && (
                    <p className="text-[10px] text-gray-500 italic">{aiSuggestions.detected_summary}</p>
                  )}

                  {toolContext === 'chord_progressions' && aiSuggestions.chord_progressions?.map((prog, i) => (
                    <div key={i} className="rounded px-2.5 py-2" style={{ background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.1)' }} data-testid={`chord-suggestion-${i}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] font-semibold text-cyan-300">{prog.name}</span>
                        <span className="text-[8px] text-cyan-500/60">{prog.energy_match}</span>
                      </div>
                      <div className="text-xs font-mono text-white/90">{prog.chords}</div>
                      <div className="text-[9px] text-gray-600 mt-0.5 leading-snug">{prog.description}</div>
                    </div>
                  ))}

                  {toolContext === 'scale_reference' && aiSuggestions.recommended_scales?.map((scale, i) => (
                    <div key={i} className="rounded px-2.5 py-2" style={{ background: 'rgba(139,92,246,0.04)', border: '1px solid rgba(139,92,246,0.1)' }} data-testid={`scale-suggestion-${i}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] font-semibold text-purple-300">{scale.name}</span>
                        <span className="text-[8px] text-purple-400/60">{scale.use_for}</span>
                      </div>
                      <div className="text-xs font-mono text-white/90">{scale.notes}</div>
                      <div className="text-[9px] text-gray-600 mt-0.5 leading-snug">{scale.why}</div>
                    </div>
                  ))}

                  {(aiSuggestions.key_insight || aiSuggestions.production_tip || aiSuggestions.synth_tip) && (
                    <div className="rounded px-2.5 py-1.5" style={{ background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.1)' }}>
                      {aiSuggestions.key_insight && <p className="text-[9px] text-amber-300/80"><strong>Insight:</strong> {aiSuggestions.key_insight}</p>}
                      {(aiSuggestions.production_tip || aiSuggestions.synth_tip) && <p className="text-[9px] text-amber-400/50 mt-0.5"><strong>Tip:</strong> {aiSuggestions.production_tip || aiSuggestions.synth_tip}</p>}
                    </div>
                  )}
                </div>
              )}

              {/* Clear */}
              <div className="flex justify-end">
                <button onClick={clearAnalysis} className="text-[9px] text-gray-600 hover:text-cyan-400 flex items-center gap-0.5" data-testid="clear-analysis-btn">
                  <Upload size={9} /> Upload different beat
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default BeatAnalyzer;
